import { ModerationSettings } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateModerationSettings } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

interface Props {
  moderationSettings: ModerationSettings
}

export const ModeratedContents = ({ moderationSettings }: Props) => {
  const { blackListPostTypeIds, blackListSpaceIds } = moderationSettings
  const enable =
    blackListPostTypeIds?.length > 0 || blackListSpaceIds?.length > 0

  const { $t } = useI18n()
  const { mutateAsync: updateModerationSettings, isLoading } =
    useUpdateModerationSettings()
  const { completeStep } = useAdminOnboarding()

  const submit = data => {
    const { blackListSpaceIds, blackListPostTypeIds, enable } = data
    updateModerationSettings({
      input: {
        blackListPostTypeIds: enable ? blackListPostTypeIds : [],
        blackListSpaceIds: enable ? blackListSpaceIds : [],
      },
    })
      .then(() => {
        completeStep({
          step: 'moderation-rules',
        })
        toast({
          title: $t({
            defaultMessage: 'Moderation settings updated.',
            id: 'Admin.ModerationSettings.Updated',
          }),
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: $t({
            defaultMessage: 'Unable to update moderation settings.',
            id: 'Admin.ModerationSettings.FailedToUpdate',
          }),
          status: 'error',
        })
      })
  }

  return (
    <Form
      defaultValues={{
        enable,
        blackListSpaceIds,
        blackListPostTypeIds,
      }}
      onSubmit={submit}
    >
      {({ formState: { isDirty }, watch }) => (
        <Card>
          <Card.Header
            title={$t({
              defaultMessage: 'Monitored contents',
              id: 'Admin.ModerationSettings.ModeratedContents',
            })}
            description={$t({
              defaultMessage:
                'Choose spaces and post types where all content will undergo a moderation check.',
              id: 'Admin.ModerationSettings.ModeratedContentsDescription',
            })}
          />
          <Card.Content>
            <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
              <Form.Toggle
                name="enable"
                label={$t({
                  defaultMessage: 'Enable monitored contents',
                  id: 'Admin.ModerationSettings.EnableMonitoredContents',
                })}
              />
              <LoadingOverlay isLoading={!watch('enable')} pulse={false}>
                <Form.SpaceIdPicker
                  multiple
                  name="blackListSpaceIds"
                  label={$t({
                    defaultMessage: 'Select spaces',
                    id: 'Generics.SelectSpaces',
                  })}
                  helperText={$t({
                    defaultMessage:
                      'Send all posts to moderation in selected spaces.',
                    id: 'Admin.ModerationSettings.ModeratedSpacesHelperText',
                  })}
                />
              </LoadingOverlay>
              <LoadingOverlay isLoading={!watch('enable')} pulse={false}>
                <Form.PostTypeIdPicker
                  multiple
                  name="blackListPostTypeIds"
                  label={$t({
                    defaultMessage: 'Select CMS models',
                    id: 'Admin.ModerationSettings.ModeratedPostTypes',
                  })}
                  helperText={$t({
                    defaultMessage:
                      'Send all posts created with selected CMS model to moderation.',
                    id: 'Admin.ModerationSettings.ModeratedPostTypesHelperText',
                  })}
                />
              </LoadingOverlay>
            </div>
          </Card.Content>
          <Card.Actions>
            <Button type="submit" loading={isLoading} disabled={!isDirty}>
              <T defaultMessage="Update" id="Generics.Update" />
            </Button>
          </Card.Actions>
        </Card>
      )}
    </Form>
  )
}
